/*
 * Messages: the client's booking texts and offers (phase 6; SF 74's row,
 * MKT 16's sheet, MKT 16b's tick; GUARDRAILS G27).
 *
 * Three choices, each per client per salon, as the API keeps them on the
 * client's link to the salon and the CMS passes them on `/api/current`:
 * - `acceptsBookingSms`: texts about their bookings (confirmations, changes,
 *   ready to collect). True unless they turn it off; off, those go by email
 *   instead (Lena, 13 Oct). Sign-in codes never stop, and a pickup contact
 *   who isn't the client is still texted.
 * - `acceptsSmsMarketing`: offers by text. Only a yes they gave counts: the
 *   tick is never ticked for them (MKT N0 rule 8; marketing plan decision 1).
 * - `acceptsMarketing`: offers by email. Never asked (null) counts as on, as
 *   mpgadmin G40 treats it; a no is kept.
 *
 * Until the API sends `acceptsBookingSms`, nothing here shows: the row and
 * the profile's entry hide, and every line reads as it did.
 */

/** The client's three choices at this salon. */
export interface Messages {
  /** Texts about their bookings. Off: email instead. */
  bookingTexts: boolean;
  /** Offers by text: true only for a yes they gave. */
  offerTexts: boolean;
  /** Offers by email: never asked reads as on. */
  offerEmails: boolean;
}

/** What `PUT /api/client/messages` takes: only what changed, never a client id (the CMS reads the token). */
export interface MessagesBody {
  acceptsBookingSms?: boolean;
  acceptsSmsMarketing?: boolean;
  acceptsMarketing?: boolean;
}

/** The server's refusal to turn booking texts off for a client with no email (Lena, 13 Oct). */
export const MESSAGES_NEED_EMAIL = 'messages-need-email';

/**
 * The choices from `/api/current` (or the save's answer), or null while the
 * API doesn't send `acceptsBookingSms`: then the row and the profile's entry
 * hide, and the site reads exactly as before.
 */
export const messagesFrom = (raw: unknown): Messages | null => {
  const r = raw as Record<string, unknown> | null;
  if (!r || typeof r !== 'object' || typeof r.acceptsBookingSms !== 'boolean') return null;
  return {
    bookingTexts: r.acceptsBookingSms,
    offerTexts: r.acceptsSmsMarketing === true,
    offerEmails: r.acceptsMarketing !== false,
  };
};

/** The client's email as `/api/current` has it, or null when there is none. */
export const emailOf = (raw: unknown): string | null => {
  const email = (raw as { email?: unknown } | null)?.email;
  return typeof email === 'string' && email.trim().includes('@') ? email.trim() : null;
};

/** SF 74's line under "Messages": "Booking texts on · offers by email, not by text". */
export const messagesLine = (m: Messages): string => {
  const booking = m.bookingTexts ? 'Booking texts on' : 'Booking texts off, email instead';
  const offers =
    m.offerTexts && m.offerEmails
      ? 'offers by email and text'
      : m.offerTexts
        ? 'offers by text, not by email'
        : m.offerEmails
          ? 'offers by email, not by text'
          : 'no offers';
  return `${booking} · ${offers}`;
};

/** A client with no email can't turn booking texts off: nothing would reach them (Lena, 13 Oct). */
export const canTurnTextsOff = (email: string | null): boolean => !!email;

export interface MessagesSheetView {
  title: string;
  sub: string;
  booking: {
    title: string;
    sub: string;
    /** A second line: where they go when off, or why they can't go off. */
    note: string | null;
    /** On, and can't be turned off (no email). */
    locked: boolean;
  };
  offerTexts: { title: string; sub: string };
  offerEmails: { title: string; sub: string; action: string };
}

/** MKT 16, for what the client has picked so far (`draft`). */
export const messagesSheet = (
  draft: Messages,
  { salonName, email }: { salonName: string; email: string | null }
): MessagesSheetView => {
  const salon = salonName.trim() || 'the salon';
  const locked = draft.bookingTexts && !canTurnTextsOff(email);
  return {
    title: 'Messages',
    sub: `Saved for your next booking at ${salon}.`,
    booking: {
      title: 'Texts about my bookings',
      // No reminder text exists (the 24-hour reminder is email only), so none is promised (Q9).
      sub: 'Confirmations, changes, and when your pets are ready',
      note: !draft.bookingTexts
        ? `Off: they come by email${email ? ` to ${email}` : ''} instead. Sign-in codes still come by text.`
        : locked
          ? `To turn these off, we need your email first, so your booking messages can go there. Ask ${salon} to add it.`
          : null,
      locked,
    },
    offerTexts: {
      title: `Text me offers from ${salon}`,
      sub: 'Optional. You can opt out from any text.',
    },
    offerEmails: draft.offerEmails
      ? {
          title: 'Offers by email are on',
          sub: email ? 'Every one has Unsubscribe at the bottom.' : 'None go until we have your email.',
          action: 'Turn off',
        }
      : {
          title: 'Offers by email are off',
          sub: `Turn them on to hear about ${salon}’s offers by email.`,
          action: 'Turn on',
        },
  };
};

/** What Done sends: only the choices that changed. Nothing changed, nothing sent. */
export const messagesChanges = (saved: Messages, draft: Messages): MessagesBody => {
  const body: MessagesBody = {};
  if (draft.bookingTexts !== saved.bookingTexts) body.acceptsBookingSms = draft.bookingTexts;
  if (draft.offerTexts !== saved.offerTexts) body.acceptsSmsMarketing = draft.offerTexts;
  if (draft.offerEmails !== saved.offerEmails) body.acceptsMarketing = draft.offerEmails;
  return body;
};

export const hasChanges = (body: MessagesBody): boolean => Object.keys(body).length > 0;

const NEED_EMAIL_FALLBACK = 'Add an email first: with booking texts off, your booking messages go by email.';
const SAVE_FALLBACK = 'That didn’t save. Please try again.';

/**
 * The words of a refusal: the server's own (`{ error, code }`, the CMS's
 * 409), else ours. Null when the body isn't a refusal.
 */
export const messagesRefusal = (body: string): { error: string; code: string | null } | null => {
  let parsed: any;
  try {
    parsed = JSON.parse(body);
  } catch {
    return null;
  }
  const code: string | null =
    typeof parsed?.code === 'string' ? parsed.code : typeof parsed?.additionalInfo?.code === 'string' ? parsed.additionalInfo.code : null;
  const words = typeof parsed?.code === 'string' ? parsed?.error : parsed?.details ?? parsed?.error;
  if (!code && typeof words !== 'string') return null;
  const fine = typeof words === 'string' && words.trim() && words.length <= 240 && !/[<>]/.test(words);
  return { error: fine ? words.trim() : code === MESSAGES_NEED_EMAIL ? NEED_EMAIL_FALLBACK : SAVE_FALLBACK, code };
};

// ── "by text" promises follow the choice (G27) ──────────────────────────
// `bookingTexts` null: the API doesn't say yet, so today's words stand.

/** The booked page, once the booking is real (SF 75): null when there's nothing true to say. */
export const detailsLine = (bookingTexts: boolean | null, mobile: string | null, email: string | null): string | null => {
  if (bookingTexts === false) return `The details are on their way${email ? ` to ${email}` : ''} by email.`;
  return mobile ? `The details are on their way to ${mobile} by text.` : null;
};

/** Moved! (SF 80). */
export const movedLine = (bookingTexts: boolean | null, mobile: string | null, email: string | null): string =>
  bookingTexts === false
    ? `The new time is on its way${email ? ` to ${email}` : ''} by email.`
    : `The new time is on its way${mobile ? ` to ${mobile}` : ''} by text.`;

/** Review and book's "Your mobile", while it's being typed. */
export const mobileLine = (bookingTexts: boolean | null): string =>
  bookingTexts === false
    ? 'You sign in with this number. Your booking details go by email.'
    : 'We text your booking details to this number.';

/** The waitlist sheet: a spot that opens comes as the salon's offer, which follows the choice. */
export const waitlistLead = (bookingTexts: boolean | null): string =>
  `Tell us when suits and we'll ${bookingTexts === false ? 'email' : 'text'} you if a spot opens. No payment now.`;

export const waitlistDoneVerb = (bookingTexts: boolean | null): 'email' | 'text' => (bookingTexts === false ? 'email' : 'text');
